import React, { useEffect, useRef, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { useFormState } from 'react-final-form';

/**
 * Background save for the processing worksheet.
 *
 * Two tier, by design:
 *
 *   - while required fields are still blank the record is written anyway and
 *     flagged is_draft = 1, so a half finished worksheet is never mistaken for
 *     a completed one on list screens or reports;
 *   - once every required field is filled the same write goes out with
 *     is_draft = 0 and the record is a normal, complete one.
 *
 * The Save button is untouched and still the way a worksheet is finished - it
 * validates, redirects, and always writes is_draft = 0.
 *
 * WHY IT LISTENS TO `modified` AND NOT `dirty`
 *
 * react-admin hands an input's `defaultValue` to final-form as a *field level*
 * defaultValue. final-form applies that to `values` but deliberately not to
 * `initialValues` (see registerField), so a form carrying any defaultValue -
 * and this one carries several, restick and the c-kit count among them - is
 * already dirty the moment it opens, before anyone has typed. Saving on
 * `dirty` would therefore write every record that was merely looked at.
 *
 * `modified` is only ever set from final-form's changeValue, which is to say
 * from a real edit or a deliberate form.change() such as the CDCS verify and
 * the soft goods lot picker. That is the signal we want.
 *
 * The value diff on top of it covers the rest: the record arriving from the
 * server, a form reinitialise, and a save that turned out to be a no-op all
 * settle the baseline without sending anything.
 */

/**
 * Long enough that a burst of typing collapses into one write, short enough
 * that it still reads as immediate. Selects, dates and the time pickers change
 * in a single step, so they effectively save as soon as the user moves on.
 */
const DEBOUNCE_MS = 1500;
/* The interval is a prop so the behaviour can be driven directly in a test
   rather than by waiting out a real second and a half. */

const statusStyle = {
    marginLeft: 16,
    fontSize: 12,
    alignSelf: 'center',
    lineHeight: 1.4,
};

const errorStyle = { color: '#b00020', fontWeight: 600 };

const pad = n => (n < 10 ? '0' + n : String(n));

const clockTime = d => pad(d.getHours()) + ':' + pad(d.getMinutes()) + ':' + pad(d.getSeconds());

/**
 * The comparison key for "has anything actually changed".
 *
 * donor_sign is left out on purpose: it is a base64 image that can run to
 * hundreds of kilobytes, it is held in a module variable rather than in form
 * state, and it never changes without some form value changing too. Hashing it
 * on every keystroke would cost far more than it is worth.
 */
const diffKey = (data) => {
    const diffable = Object.assign({}, data);
    delete diffable.donor_sign;
    return JSON.stringify(diffable);
};

/**
 * `externalKey` / `externalDirty` cover the parts of the payload that do not
 * live in form state - today the donor reaction report, which is held in React
 * state so that opening a worksheet does not mark it dirty. Without them a
 * change made in the incident dialog would never reach the server: the
 * component would re-render, but the effect below watches form state and would
 * not see anything move.
 *
 * `externalKey` changes when that data changes, which re-runs the effect.
 * `externalDirty` says a person changed it, which is what separates a real
 * edit from the initial load - exactly the distinction `modified` draws for
 * the form itself.
 */
const AutoSave = ({
    record,
    resource,
    transform,
    debounceMs = DEBOUNCE_MS,
    externalKey = '',
    externalDirty = false,
}) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();

    const { values, valid, modified, submitting, submitSucceeded } = useFormState({
        subscription: {
            values: true,
            valid: true,
            modified: true,
            submitting: true,
            submitSucceeded: true,
        },
    });

    const id = record ? record.id : undefined;

    /** Serialised payload we believe the server already holds. */
    const baselineRef = useRef(null);
    /** Most recent payload built from form state, saved or not. */
    const latestRef = useRef(null);
    const inFlightRef = useRef(false);
    const timerRef = useRef(null);
    const mountedRef = useRef(true);
    /** Rebuilt every render so the flush never closes over stale props. */
    const flushRef = useRef(null);
    const transformRef = useRef(transform);
    transformRef.current = transform;

    const [status, setStatus] = useState('idle');
    const [savedAt, setSavedAt] = useState(null);
    const [savedDraft, setSavedDraft] = useState(false);

    const setStatusSafe = (next) => {
        if (mountedRef.current) {
            setStatus(next);
        }
    };

    flushRef.current = () => {
        // One request at a time. Anything that arrives while this one is in
        // flight is picked up by the re-check in the success handler, which is
        // what stops an older payload landing on top of a newer one.
        if (inFlightRef.current) {
            return;
        }
        if (id === undefined || id === null) {
            return;
        }

        const latest = latestRef.current;
        if (!latest || latest.key === baselineRef.current) {
            return;
        }

        const sentKey = latest.key;
        const sentDraft = latest.isDraft;

        inFlightRef.current = true;
        setStatusSafe('saving');

        dataProvider
            .update(resource, { id: id, data: latest.data, previousData: record })
            .then(() => {
                inFlightRef.current = false;
                baselineRef.current = sentKey;

                if (mountedRef.current) {
                    setStatus('saved');
                    setSavedAt(new Date());
                    setSavedDraft(sentDraft === 1);
                }

                // Edits made while that request was out.
                if (latestRef.current && latestRef.current.key !== sentKey) {
                    flushRef.current();
                }
            })
            .catch((error) => {
                inFlightRef.current = false;
                // Baseline deliberately not advanced, so the next edit retries
                // this payload rather than silently dropping it.
                setStatusSafe('error');
                notify(
                    'Background save failed, your latest changes are not saved: ' +
                        (error && error.message ? error.message : 'unknown error'),
                    { type: 'warning' }
                );
            });
    };

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    /** A different record in the same mounted form starts from a clean slate. */
    useEffect(() => {
        baselineRef.current = null;
        latestRef.current = null;
        setStatus('idle');
        setSavedAt(null);
        setSavedDraft(false);
    }, [id]);

    useEffect(() => {
        // The Save button owns the submit path: it validates, writes
        // is_draft = 0 and redirects. Autosave stays out of its way.
        if (submitting || submitSucceeded) {
            return;
        }

        const isDraft = valid ? 0 : 1;
        const data = Object.assign({}, transformRef.current(values), { is_draft: isDraft });
        const key = diffKey(data);
        const anyModified = (modified
            ? Object.keys(modified).some(name => modified[name])
            : false) || externalDirty;

        if (!anyModified) {
            // Not a user edit: the record loading in, a reinitialise, or the
            // defaultValue injection described at the top. Adopt it as the
            // baseline so it is never mistaken for something to save.
            baselineRef.current = key;
            latestRef.current = { key: key, data: data, isDraft: isDraft };
            return;
        }

        latestRef.current = { key: key, data: data, isDraft: isDraft };

        if (key === baselineRef.current) {
            return;
        }

        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            flushRef.current();
        }, debounceMs);
    }, [values, valid, modified, submitting, submitSucceeded, debounceMs, externalKey, externalDirty]);

    /**
     * Leaving the screen with a debounce still counting down would throw away
     * the last second and a half of typing, which is the one thing this
     * component exists to prevent. Send it instead.
     *
     * Declared after the mountedRef effect so that effect's cleanup has already
     * cleared the flag - the request still goes out, it just skips the state
     * updates it can no longer apply.
     */
    useEffect(() => () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
            flushRef.current();
        }
    }, []);

    if (status === 'saving') {
        return <span style={statusStyle}>Saving...</span>;
    }
    if (status === 'error') {
        return <span style={statusStyle}><span style={errorStyle}>Not saved</span></span>;
    }
    if (status === 'saved' && savedAt) {
        return (
            <span style={statusStyle}>
                {(savedDraft ? 'Draft saved ' : 'Saved ') + clockTime(savedAt)}
            </span>
        );
    }
    return <span style={statusStyle} />;
};

export default AutoSave;
